/**
 * HeatmapTool - 히트맵 시각화 도구
 * OpenLayers Heatmap 레이어를 사용하여 포인트 밀도 시각화
 */

import Heatmap from 'ol/layer/Heatmap';
import VectorSource from 'ol/source/Vector';
import { layerManager } from '../core/LayerManager.js';
import { collectNumericFields } from '../utils/layerSelect.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { mapManager } from '../core/MapManager.js';
import { eventBus, Events } from '../utils/EventBus.js';
import { syncLegendVisibility } from './legendVisibility.js';
import { makeDraggable } from '../utils/DraggableElement.js';

class HeatmapTool {
  constructor() {
    this.heatmapLayers = new Map(); // layerId -> heatmapLayer
    this.legends = new Map(); // layerId -> legend element
    this.initEventListeners();
  }

  /**
   * 이벤트 리스너 초기화
   */
  initEventListeners() {
    // 레이어 삭제 시 범례도 함께 제거
    eventBus.on(Events.LAYER_REMOVED, (data) => {
      const { layerId } = data;
      if (this.heatmapLayers.has(layerId)) {
        this.heatmapLayers.delete(layerId);
        this.removeLegend(layerId);
      }
    });

    // 레이어 이름 변경 → 히트맵 범례 제목 동기화
    eventBus.on(Events.LAYER_RENAMED, (data) => {
      if (!data || !data.layerId) return;
      const legendEl = this.legends.get(data.layerId);
      if (!legendEl) return;
      const titleEl = legendEl.querySelector('.heatmap-legend-title');
      if (titleEl && titleEl.textContent !== data.name) titleEl.textContent = data.name;
    });
  }

  /**
   * 히트맵 생성
   * @param {string} layerId - 소스 포인트 레이어 ID
   * @param {Object} options - 히트맵 옵션
   * @returns {string} 히트맵 레이어 ID
   */
  createHeatmap(layerId, options = {}) {
    const {
      blur = 15,
      radius = 10,
      weight = null, // 가중치 필드명
      gradient = null // 커스텀 그라디언트
    } = options;

    const layerInfo = layerManager.getLayer(layerId);
    if (!layerInfo) {
      throw new Error('레이어를 찾을 수 없습니다.');
    }

    // 포인트 레이어만 지원
    if (layerInfo.geometryType !== 'Point' && layerInfo.geometryType !== 'MultiPoint') {
      throw new Error('히트맵은 포인트 레이어에서만 생성할 수 있습니다.');
    }

    const source = layerInfo.olLayer.getSource();
    const features = source.getFeatures();

    if (features.length === 0) {
      throw new Error('레이어에 피처가 없습니다.');
    }

    // 새로운 VectorSource 생성 (원본 복사)
    const heatmapSource = new VectorSource({
      features: features.map(f => f.clone())
    });

    // 히트맵 레이어 옵션
    const heatmapOptions = {
      source: heatmapSource,
      blur: blur,
      radius: radius,
      zIndex: 500
    };

    // 가중치 함수 설정
    if (weight) {
      heatmapOptions.weight = (feature) => {
        const value = feature.get(weight);
        if (typeof value === 'number') {
          // 0-1 사이로 정규화
          return Math.min(Math.max(value / 100, 0), 1);
        }
        return 1;
      };
    }

    // 커스텀 그라디언트
    if (gradient) {
      heatmapOptions.gradient = gradient;
    }

    // 같은 원본 레이어로 만든 기존 히트맵 제거 (재적용 시 교체 — 레이어·범례 중복 방지)
    const staleIds = [];
    for (const [existingId, info] of this.heatmapLayers) {
      if (info.sourceLayerId === layerId) staleIds.push(existingId);
    }
    staleIds.forEach(id => this.removeHeatmap(id));

    // 히트맵 레이어 생성
    const heatmapLayer = new Heatmap(heatmapOptions);

    // LayerManager에 히트맵 레이어 등록
    const heatmapLayerId = layerManager.addLayer({
      name: `${layerInfo.name}_히트맵`,
      type: 'heatmap',
      olLayer: heatmapLayer, // 기존 레이어 전달
      geometryType: 'Point',
      visible: true
    });

    // 히트맵 레이어 정보 저장
    this.heatmapLayers.set(heatmapLayerId, {
      layer: heatmapLayer,
      sourceLayerId: layerId,
      options: options
    });

    // 프로젝트(.egis)·자동저장 왕복을 위해 레이어에 설정 저장 (주제도 3종과 동일 규약)
    const registeredInfo = layerManager.getLayer(heatmapLayerId);
    if (registeredInfo) {
      registeredInfo._heatmapConfig = {
        sourceLayerId: layerId,
        blur,
        radius,
        weight: weight || null,
        gradient: gradient || null,
        hideSource: options.hideSource || false
      };
    }

    // 원래 레이어 숨기기 옵션
    // layerInfo.visible까지 같이 내려야 한다. 지도에서만 감추면 레이어 목록의
    // 체크는 켜진 채로 남아, 켜져 있는데 안 보이는 상태가 된다.
    if (options.hideSource) {
      layerInfo.olLayer.setVisible(false);
      layerInfo.visible = false;
    }

    // 범례 생성
    this.createLegend(heatmapLayerId, layerInfo.name, gradient || this.getGradients()[0].value);

    return heatmapLayerId;
  }

  /**
   * 프로젝트(.egis) 복원용 — 저장된 피처와 설정으로 히트맵 레이어를 재생성한다.
   * (원본 포인트 레이어가 없어도 히트맵 자체 피처로 복원 가능)
   * @param {import('ol/Feature').default[]} features - EPSG:3857 좌표의 포인트 피처들
   * @param {Object} layerData - .egis 레이어 데이터 (id, name, visible, opacity, heatmapConfig)
   * @returns {string} 히트맵 레이어 ID
   */
  restoreHeatmap(features, layerData) {
    const cfg = layerData.heatmapConfig || {};

    const heatmapSource = new VectorSource({ features });

    const heatmapOptions = {
      source: heatmapSource,
      blur: cfg.blur != null ? cfg.blur : 15,
      radius: cfg.radius != null ? cfg.radius : 10,
      zIndex: 500
    };

    if (cfg.weight) {
      heatmapOptions.weight = (feature) => {
        const value = feature.get(cfg.weight);
        if (typeof value === 'number') {
          return Math.min(Math.max(value / 100, 0), 1);
        }
        return 1;
      };
    }

    if (cfg.gradient) {
      heatmapOptions.gradient = cfg.gradient;
    }

    const heatmapLayer = new Heatmap(heatmapOptions);

    const heatmapLayerId = layerManager.addLayer({
      id: layerData.id,
      name: layerData.name,
      type: 'heatmap',
      olLayer: heatmapLayer,
      geometryType: 'Point',
      visible: layerData.visible !== false
    });

    this.heatmapLayers.set(heatmapLayerId, {
      layer: heatmapLayer,
      sourceLayerId: cfg.sourceLayerId || null,
      options: {
        blur: cfg.blur,
        radius: cfg.radius,
        weight: cfg.weight || null,
        gradient: cfg.gradient || null,
        hideSource: cfg.hideSource || false
      }
    });

    const layerInfo = layerManager.getLayer(heatmapLayerId);
    if (layerInfo) {
      layerInfo._heatmapConfig = {
        sourceLayerId: cfg.sourceLayerId || null,
        blur: cfg.blur,
        radius: cfg.radius,
        weight: cfg.weight || null,
        gradient: cfg.gradient || null,
        hideSource: cfg.hideSource || false
      };

      // 불투명도 복원
      if (typeof layerData.opacity === 'number' && typeof heatmapLayer.setOpacity === 'function') {
        layerInfo.opacity = layerData.opacity;
        heatmapLayer.setOpacity(layerData.opacity);
      }
    }

    // 범례 생성 (레이어명에서 '_히트맵' 접미사 제거해 원본 이름으로 표시)
    const baseName = (layerData.name || '').replace(/_히트맵$/, '');
    this.createLegend(heatmapLayerId, baseName, cfg.gradient || this.getGradients()[0].value);

    return heatmapLayerId;
  }

  /**
   * 범례 생성
   */
  createLegend(layerId, layerName, gradient) {
    this.removeLegend(layerId);

    const legendEl = document.createElement('div');
    legendEl.className = 'heatmap-legend';
    legendEl.id = `heatmap-legend-${layerId}`;

    const gradientCSS = `linear-gradient(to right, ${gradient.join(', ')})`;

    legendEl.innerHTML = `
      <div class="heatmap-legend-title">${escapeHtml(layerName)} 히트맵</div>
      <div class="heatmap-legend-bar" style="background: ${gradientCSS}"></div>
      <div class="heatmap-legend-labels">
        <span>낮음</span>
        <span>높음</span>
      </div>
    `;

    const mapContainer = document.getElementById('map');
    if (mapContainer) {
      mapContainer.appendChild(legendEl);
      this.legends.set(layerId, legendEl);
      makeDraggable(legendEl, () => mapContainer);
      // 복원처럼 숨긴 레이어의 범례를 만들 때도 레이어 가시성을 따른다
      syncLegendVisibility(layerId);
    }
  }

  /**
   * 범례 제거
   */
  removeLegend(layerId) {
    const legendEl = this.legends.get(layerId);
    if (legendEl) {
      legendEl.remove();
      this.legends.delete(layerId);
    }
  }

  /**
   * 히트맵 설정 업데이트
   * @param {string} heatmapId - 히트맵 레이어 ID
   * @param {Object} options - 새 옵션
   */
  updateHeatmap(heatmapId, options) {
    const heatmapInfo = this.heatmapLayers.get(heatmapId);
    if (!heatmapInfo) return;

    const { layer } = heatmapInfo;

    if (options.blur !== undefined) {
      layer.setBlur(options.blur);
    }

    if (options.radius !== undefined) {
      layer.setRadius(options.radius);
    }

    if (options.gradient !== undefined) {
      layer.setGradient(options.gradient);
    }
  }

  /**
   * 히트맵 제거
   * @param {string} heatmapId - 히트맵 레이어 ID
   */
  removeHeatmap(heatmapId) {
    const heatmapInfo = this.heatmapLayers.get(heatmapId);
    if (!heatmapInfo) return;

    // LayerManager를 통해 제거
    layerManager.removeLayer(heatmapId);
    this.heatmapLayers.delete(heatmapId);

    // 범례 제거
    this.removeLegend(heatmapId);
  }

  /**
   * 포인트 레이어 목록 가져오기
   */
  getPointLayers() {
    return layerManager.getAllLayers().filter(layer => {
      return layer.geometryType === 'Point' || layer.geometryType === 'MultiPoint';
    });
  }

  /**
   * 미리 정의된 그라디언트 목록
   */
  getGradients() {
    return [
      {
        name: '기본 (파랑-빨강)',
        value: ['#00f', '#0ff', '#0f0', '#ff0', '#f00']
      },
      {
        name: '열화상',
        value: ['#000', '#800080', '#ff0000', '#ffff00', '#ffffff']
      },
      {
        name: '초록색 계열',
        value: ['#ffffcc', '#c2e699', '#78c679', '#31a354', '#006837']
      },
      {
        name: '파랑색 계열',
        value: ['#f7fbff', '#deebf7', '#c6dbef', '#6baed6', '#2171b5']
      },
      {
        name: '단색 (빨강)',
        value: ['rgba(255,0,0,0)', 'rgba(255,0,0,0.5)', 'rgba(255,0,0,1)']
      }
    ];
  }

  /**
   * 사용 가능한 숫자 필드 목록 가져오기
   */
  getNumericFields(layerId) {
    const layerInfo = layerManager.getLayer(layerId);
    if (!layerInfo) return [];

    const source = layerInfo.olLayer.getSource();
    return collectNumericFields(source ? source.getFeatures() : []);
  }
}

export const heatmapTool = new HeatmapTool();
